import type { ReactNode } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import NoAccess from '@/components/common/NoAccess'
import AppShell from '@/components/layout/AppShell'
import { accessOf, canFinance, canManageUsers, canOpening, canSettlement } from '@/lib/admin-access'
import { hasOAuthCallbackParams } from '@/lib/auth'
import { AccSumPage } from '@/pages/AccSumPage'
import { AgreementsPage } from '@/pages/AgreementsPage'
import { AgreementReviewPage } from '@/pages/AgreementReviewPage'
import { BanksPage } from '@/pages/BanksPage'
import { BcaCsvPage } from '@/pages/BcaCsvPage'
import { ClientBanksPage } from '@/pages/ClientBanksPage'
import { FundsQueuePage } from '@/pages/FundsQueuePage'
import { DailyPage } from '@/pages/DailyPage'
import { FirstDepositsPage } from '@/pages/FirstDepositsPage'
import { LiveAccountsPage } from '@/pages/LiveAccountsPage'
import { LoginPage } from '@/pages/LoginPage'
import { ActivityLogsPage, EkycLogsPage } from '@/pages/LogsPages'
import { ManageUsersPage } from '@/pages/ManageUsersPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { RatesPage } from '@/pages/RatesPage'
import { DashboardPage, OpeningHomePage, SettlementHomePage, useHomePath } from '@/pages/WorkspaceHome'
import { useAuthStore } from '@/store/auth'

function SigningIn() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-sidebar text-sm text-sidebar-foreground" role="status">
      Completing sign-in…
    </div>
  )
}

function Protected({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()
  if (isLoading || hasOAuthCallbackParams()) {
    return <SigningIn />
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

// Checks every Cognito group. Aliases such as "settlement" match backoffice-service app/core/security.py ROLE_ALIASES. The API still enforces the role.
const openingOrSettlement = (role?: string | string[]) => canOpening(role) || canSettlement(role)
const financeDesk = (role?: string | string[]) => canFinance(role)
const activityDesk = (role?: string | string[]) => canOpening(role) || canFinance(role)

function RequireAccess({ allow, children }: { allow: (role?: string | string[]) => boolean; children: ReactNode }) {
  const user = useAuthStore((s) => s.user)
  return allow(accessOf(user)) ? <>{children}</> : <NoAccess />
}

const guard = (allow: (role?: string | string[]) => boolean, el: ReactNode) => (
  <RequireAccess allow={allow}>{el}</RequireAccess>
)

function HomeRedirect() {
  const { isAuthenticated, isLoading } = useAuthStore()
  const home = useHomePath()
  if (isLoading || hasOAuthCallbackParams()) {
    return <SigningIn />
  }
  return <Navigate to={isAuthenticated ? home : '/login'} replace />
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route path="/dash" element={guard(canManageUsers, <DashboardPage />)} />
        <Route path="/accsum" element={guard(canSettlement, <AccSumPage />)} />
        <Route path="/opening" element={guard(canOpening, <OpeningHomePage />)} />
        <Route path="/opening/agreements" element={guard(canOpening, <AgreementsPage />)} />
        <Route path="/opening/agreements/:id" element={guard(canOpening, <AgreementReviewPage />)} />
        <Route path="/settlement" element={guard(financeDesk, <SettlementHomePage />)} />
        <Route path="/settlement/funds" element={guard(financeDesk, <FundsQueuePage />)} />
        <Route path="/settlement/bca" element={guard(financeDesk, <BcaCsvPage />)} />
        <Route path="/settlement/accounts" element={guard(canSettlement, <LiveAccountsPage defaultType="real" />)} />
        <Route path="/settlement/client-banks" element={guard(canSettlement, <ClientBanksPage />)} />
        <Route path="/settlement/banks" element={guard(financeDesk, <BanksPage />)} />
        <Route path="/settlement/rates" element={guard(financeDesk, <RatesPage />)} />
        <Route path="/reports/mt-users" element={guard(canSettlement, <LiveAccountsPage title="MT Users" description="Every MT5 account, live and demo, linked to its client." />)} />
        {/* First deposits / Daily get real pages in Phase 6; redirects stay until then. */}
        <Route path="/reports/first-deposits" element={guard(canSettlement, <FirstDepositsPage />)} />
        <Route path="/reports/daily" element={guard(canSettlement, <DailyPage />)} />
        <Route path="/reports/login-state" element={guard(canSettlement, <LiveAccountsPage title="Login State" description="Whether each live login is active and when it last synced." forceType="real" />)} />
        <Route path="/logs/activity" element={guard(activityDesk, <ActivityLogsPage />)} />
        <Route path="/logs/ekyc" element={guard(openingOrSettlement, <EkycLogsPage />)} />
        <Route path="/users" element={guard(canManageUsers, <ManageUsersPage />)} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      <Route path="/" element={<HomeRedirect />} />
    </Routes>
  )
}
